/* eslint-disable max-lines */
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Region } from '@components/common/constants';
import { getApiErrorMessage, getApiPureErrorMessage } from '@components/common/util/util';
import { apiAuthCaller } from '@services/apiCaller';
import { resumeSubscription as resumeSubscriptionApi } from '@api';
import { IRootStore } from '@stores/index';
import type {
  fromTo,
  ICardDetails,
  IBillingInfoResponse,
  IBillingURL,
  IInvoicePreview,
  IPaymentMethod,
  ISubscriptionSchedule,
  ITenantInfo,
  SubscriptionResponse,
} from './types';
import { InvoicesStore } from './invoicesStore';
import { confirmAndSettleRetry, confirmSubscriptionPayment } from './subscriptionPaymentClient';
import {
  GENERIC_PAYMENT_FAILURE_MESSAGE,
  PaymentFailedError,
  isPaymentRequiredError,
  isSubscriptionPaymentIncomplete,
  preferPaymentFailureReason,
} from './paymentErrors';

export type { IInvoicePreview, IPaymentMethod, IPromotionDetails, PlanNameType } from './types';

dayjs.extend(utc);

export interface IBillingStore {
  rootStore: IRootStore;
  billingTenure: fromTo | undefined;
  planName: ITenantInfo['planName'] | '';
  price: number;
  amountDue: number;
  paymentMethods: IPaymentMethod[];
  plansDetails: IPlanDetails[];
  cancelAtPeriodEnd: boolean;
  subscriptionSchedule: ISubscriptionSchedule | null;
  loadingPayments: boolean;
  getPlanName: () => void;
  redirectToBilling: () => Promise<void>;
  updatePaymentMethod: (
    id: string,
    cardDetails: ICardDetails,
    isSuccess?: boolean,
  ) => Promise<void>;
  makeDefaultPayment: (isDefault: boolean, paymentMethodId?: string) => Promise<void>;
  refreshAfterCardAdded: (makeDefault: boolean, paymentMethodId?: string) => Promise<void>;
  updateDefaultBillingEmail: (email: string) => void;
  updateSubscription: (stripePlanId: string) => Promise<void>;
  updateSubscriptionV2: (stripePlanId: string, promotionCode?: string) => Promise<void>;
  retrySubscriptionPayment: () => Promise<void>;
  cancelSubscription: () => Promise<void>;
  cancelSubscriptionV2: () => Promise<void>;
  invoicesStore: InvoicesStore;
  isResumingSubscription: boolean;
  resumeSubscription: () => Promise<void>;
  invoicePreview: IInvoicePreview | null;
  loadingInvoicePreview: boolean;
  clearInvoicePreview: () => void;
  getInvoicePreview: (stripePlanId: string, promotionCode?: string) => Promise<void>;
  deletePaymentMethod: (paymentMethod: IPaymentMethod) => Promise<void>;
  getPaymentMethods: () => Promise<void>;
  getSelfServePlans: () => Promise<void>;
  getTenantDetail: () => Promise<void>;
  tenantInfo: ITenantInfo | undefined;
  firstLoad: boolean;
  nextBillingDate: number | undefined;
  currentPlan?: IPlanDetails;
  isFreeTier: boolean;
  growthPaidUsageStartAt: string | null;
  currentPriceId: string | undefined;
  currentBillingCycle: 'month' | 'year';
  reset: () => void;
  selfServePlansLoading: boolean;
}

export interface IPlanDetails {
  id: string;
  productName: string;
  planType: 'Starter' | 'Free Tier' | 'Growth';
  stripePriceId: string;
  unitAmount: number;
  eventVolume: number;
  features?: PlanFeatureMap;
  prices: {
    id: string;
    recurringInterval: 'month' | 'year';
    unitAmount: number;
    currency: string;
  }[];
}

type PlanFeatureValue = string | number | boolean | null | undefined;

export type PlanFeatureMap = Record<string, PlanFeatureValue>;

const billingCycleStartDate = dayjs().utc().startOf('month').unix();
const billingCycleEndDate = dayjs().add(1, 'month').utc().startOf('month').unix();

export class BillingStore implements IBillingStore {
  @observable public rootStore: IRootStore;

  @observable public planName: ITenantInfo['planName'] | '' = '';

  // used for only starter plan price in plans tab in settings page
  @observable public price = 0;

  @observable public amountDue = 0;

  @observable public paymentMethods: IPaymentMethod[] = [];

  @observable public plansDetails: IPlanDetails[] = [];

  @observable public loadingPayments = false;

  @observable public cancelAtPeriodEnd = false;

  @observable public subscriptionSchedule: ISubscriptionSchedule | null = null;

  @observable public invoicePreview: IInvoicePreview | null = null;

  @observable public loadingInvoicePreview = false;

  @observable public isResumingSubscription = false;

  @observable public currentPriceId: string | undefined = undefined;

  @observable tenantInfo: ITenantInfo | undefined;

  @observable billingTenure: fromTo | undefined = {
    startDate: billingCycleStartDate * 1000,
    endDate: billingCycleEndDate * 1000,
  };

  @observable firstLoad = false;

  public readonly invoicesStore: InvoicesStore;

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.invoicesStore = new InvoicesStore(rootStore);

    // Re-fetch the event count once the Growth paid-usage anchor resolves (it arrives
    // from /tenant independently of the count fetch), so first-load usage reflects the
    // clamped window without a manual refresh.
    reaction(
      () => this.growthPaidUsageStartAt,
      (anchor) => {
        if (anchor) {
          this.rootStore.organizationStore.loadTotalEventsCount();
        }
      },
    );
  }

  @action.bound
  // get the plan type, the number of credits and other billing info
  public async getPlanName() {
    const { messagesStore, featuresStore } = this.rootStore;
    if (!featuresStore.has('BILLING_PAGE')) {
      return;
    }

    if (!this.tenantInfo) {
      await this.getTenantDetail();
    }

    await this.getSelfServePlans();

    if (this.isFreeTier) {
      const currentPlanStartsAt = dayjs(this.tenantInfo?.currentPlanStartAt)
        .utc()
        .unix();
      const startDate =
        billingCycleStartDate >= currentPlanStartsAt ? billingCycleStartDate : currentPlanStartsAt;
      this.updateBillingDates(startDate, billingCycleEndDate);
      return;
    }

    try {
      const resp: AxiosResponse<IBillingInfoResponse> = await apiAuthCaller({
        region: Region.US,
      }).get('/billing');

      this.updateBillingInfo(resp.data);
    } catch (error) {
      if (featuresStore.has('BILLING_PAGE')) {
        messagesStore.showErrorMessage('Failed to get billing details');
      }
    }
  }

  @action.bound
  private updateBillingInfo(subscriptionInfo: IBillingInfoResponse) {
    const {
      name,
      currentPeriodStartAt,
      currentPeriodEndAt,
      price,
      cancelAtPeriodEnd,
      currentPriceId,
      subscriptionSchedule,
    } = subscriptionInfo;
    this.planName = name;
    this.price = price;
    this.currentPriceId = currentPriceId;
    if (this.rootStore.featuresStore.has('BILLING_PAGE')) {
      this.cancelAtPeriodEnd = cancelAtPeriodEnd;
      this.subscriptionSchedule = subscriptionSchedule;
      this.updateBillingDates(currentPeriodStartAt, currentPeriodEndAt);
    }
  }

  @action.bound
  public reset() {
    this.planName = '';
    this.price = 0;
    this.amountDue = 0;
    this.paymentMethods = [];
    this.plansDetails = [];
    this.loadingPayments = false;
    this.cancelAtPeriodEnd = false;
    this.subscriptionSchedule = null;
    this.currentPriceId = undefined;
    this.tenantInfo = undefined;
    this.billingTenure = undefined;
    this.firstLoad = false;
    this.invoicePreview = null;
    this.loadingInvoicePreview = false;
    this.isResumingSubscription = false;
    this.invoicesStore.reset();
  }

  @action.bound
  private finishResumeSubscription(success: boolean) {
    if (success) {
      this.cancelAtPeriodEnd = false;
      this.subscriptionSchedule = null;
    }
    this.isResumingSubscription = false;
  }

  @action.bound
  public async resumeSubscription(): Promise<void> {
    if (this.isResumingSubscription) {
      return;
    }
    const { organizationStore, messagesStore } = this.rootStore;
    const organizationId = organizationStore.id;
    if (!organizationId) {
      return;
    }
    this.isResumingSubscription = true;
    try {
      await resumeSubscriptionApi(organizationId);
      this.finishResumeSubscription(true);
    } catch (error) {
      this.finishResumeSubscription(false);
      messagesStore.showErrorMessage(getApiErrorMessage(error, 'Failed to resume subscription'));
    }
  }

  @action.bound
  public async updateDefaultBillingEmail(email: string) {
    const {
      organizationStore: { id: orgId },
      messagesStore,
    } = this.rootStore;
    try {
      await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).put(
        `organizations/${orgId}/billing/information`,
        {
          billingEmail: email,
        },
      );
      if (this.tenantInfo) {
        this.tenantInfo.invoiceEmails = [email];
      }
      messagesStore.showSuccessMessage('Successfully updated billing email');
    } catch (e) {
      messagesStore.showErrorMessage('Unable to update billing email');
    }
  }

  @observable selfServePlansLoading = false;

  @action.bound
  public async getSelfServePlans() {
    const {
      organizationStore: { id: orgId },
      featuresStore,
      messagesStore,
    } = this.rootStore;
    try {
      this.selfServePlansLoading = true;

      if (featuresStore.has('BILLING_PAGE')) {
        const response = await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).get(
          `organizations/${orgId}/plans/selfServe`,
        );
        this.plansDetails = response.data.sort(
          (a: IPlanDetails, b: IPlanDetails) => a.eventVolume - b.eventVolume,
        );
      }
    } catch (e) {
      messagesStore.showErrorMessage('Unable get plans details');
    } finally {
      this.selfServePlansLoading = false;
    }
  }

  @action.bound
  public async getTenantDetail() {
    const { featuresStore, userStore } = this.rootStore;
    if ((!featuresStore.has('BILLING') && !featuresStore.has('USAGE')) || !userStore.isOrgAdmin) {
      this.firstLoad = true;
      return;
    }

    try {
      const resp = await apiAuthCaller({ region: Region.US })
        .ignore400()
        .get<ITenantInfo>('/tenant');
      this.tenantInfo = resp.data;
      if (featuresStore.has('USAGE_PAGE') && !featuresStore.has('BILLING_PAGE')) {
        this.updateBillingDates(billingCycleStartDate, billingCycleEndDate);
      }
    } catch (err) {
      // handle error
    } finally {
      this.firstLoad = true;
    }
  }

  private updateBillingDates(start: number, end: number) {
    this.billingTenure = { startDate: start * 1000, endDate: end * 1000 };
  }

  @action.bound
  public async redirectToBilling() {
    try {
      const resp: AxiosResponse<IBillingURL> = await apiAuthCaller({ region: Region.US }).get(
        `/billing/url?callbackUrl=${window.location.href}`,
      );
      window.location.href = resp.data.url;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to redirect to payment page');
      this.rootStore.messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public async getPaymentMethods() {
    try {
      const {
        organizationStore: { id },
      } = this.rootStore;
      this.loadingPayments = true;
      const resp: AxiosResponse<IPaymentMethod[]> = await apiAuthCaller({
        nonWorkspaceRoute: true,
        region: Region.US,
      }).get(`organizations/${id}/paymentMethods`);
      this.paymentMethods = resp.data;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to get Plans Details');
      this.rootStore.messagesStore.showErrorMessage(message);
    } finally {
      this.loadingPayments = false;
    }
  }

  @action.bound
  public async updatePaymentMethod(id: string, cardDetails: ICardDetails, makeDefault?: boolean) {
    const {
      messagesStore,
      organizationStore: { id: orgId },
    } = this.rootStore;
    try {
      await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).patch(
        `organizations/${orgId}/paymentMethods/${id}`,
        cardDetails,
      );
      if (makeDefault) {
        messagesStore.showSuccessMessage(
          `Added card details successfully and made it as default payment method`,
        );
      } else {
        messagesStore.showSuccessMessage('Updated card details successfully');
      }
      await this.getTenantDetail();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to update card details');
      messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public async makeDefaultPayment(isDefault: boolean, paymentMethodId?: string) {
    const { messagesStore } = this.rootStore;
    try {
      await this.getPaymentMethods();
      if (!(isDefault || this.paymentMethods.length === 1)) {
        return;
      }
      // Prefer the just-confirmed card by id; the legacy redirect path (no id)
      // falls back to the first entry as before.
      const target = paymentMethodId
        ? this.paymentMethods.find((paymentMethod) => paymentMethod.id === paymentMethodId)
        : this.paymentMethods[0];
      if (!target) {
        // Stripe attach can lag the refetch, or the id isn't in the list yet —
        // refresh the tenant so the card still surfaces instead of dereferencing
        // an empty/missing entry (which previously threw again inside catch).
        await this.getTenantDetail();
        return;
      }
      await this.updatePaymentMethod(target.id, { setDefault: true }, true);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to set default payment method');
      messagesStore.showErrorMessage(message);
    }
  }

  // Refresh the saved card in place after an in-page Stripe confirmation, so
  // callers can keep their drawer/section open instead of a full-page redirect.
  @action.bound
  public async refreshAfterCardAdded(makeDefault: boolean, paymentMethodId?: string) {
    if (makeDefault) {
      await this.makeDefaultPayment(true, paymentMethodId);
    } else {
      await this.getPaymentMethods();
      await this.getTenantDetail();
      this.rootStore.messagesStore.showSuccessMessage('Added card details successfully');
    }
  }

  @action.bound
  public async updateSubscription(stripePriceId: string) {
    try {
      await this.updateSubscriptionV2(stripePriceId);
    } catch (error) {
      const fallback = getApiErrorMessage(error, 'Failed to update subscription');
      this.rootStore.messagesStore.showErrorMessage(preferPaymentFailureReason(error, fallback));
    }
  }

  @action.bound
  public async updateSubscriptionV2(stripePriceId: string, promotionCode?: string) {
    const {
      messagesStore,
      organizationStore: { id: orgId },
    } = this.rootStore;
    const caller = apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US });
    const path = `organizations/${orgId}/subscriptions`;
    const payload = { stripePriceId, ...(promotionCode && { promotionCode }) };
    let data: SubscriptionResponse;
    try {
      ({ data } = this.isFreeTier
        ? await caller.post<SubscriptionResponse>(path, payload)
        : await caller.put<SubscriptionResponse>(path, payload));
    } catch (error) {
      // A strict (atomic-create) decline: 402 with a customer-safe reason, nothing persisted.
      if (isPaymentRequiredError(error)) {
        const reason = getApiPureErrorMessage(error, GENERIC_PAYMENT_FAILURE_MESSAGE);
        throw new PaymentFailedError(reason);
      }
      throw error;
    }

    // The finally refresh keeps the unpaid-invoice banner in sync on every outcome.
    try {
      if (data.paymentStatus === 'requires_action') {
        // Complete SCA/3DS on the client; throws PaymentFailedError on any non-succeeded outcome.
        await confirmSubscriptionPayment(data);
        await this.handleSubscriptionSuccess(data);
        messagesStore.showSuccessMessage('Payment confirmed and subscription updated successfully');
        return;
      }

      if (isSubscriptionPaymentIncomplete(data)) {
        // Payment did not go through and the plan was NOT changed: surface the reason and let the
        // caller offer a retry rather than showing a misleading success.
        throw new PaymentFailedError(data.declineReason ?? GENERIC_PAYMENT_FAILURE_MESSAGE);
      }

      await this.handleSubscriptionSuccess(data);
      messagesStore.showSuccessMessage('Updated subscription successfully');
    } finally {
      await this.invoicesStore.getInvoices();
    }
  }

  // Retries payment on the latest open invoice (declined upgrade/renewal). Drives 3DS when needed,
  // throws PaymentFailedError with the reason on decline, and refreshes the unpaid-invoice banner.
  @action.bound
  public async retrySubscriptionPayment() {
    const { messagesStore, organizationStore } = this.rootStore;
    const retryPayment = () =>
      apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).put<SubscriptionResponse>(
        `organizations/${organizationStore.id}/subscriptions/retry`,
      );
    const { data } = await retryPayment();

    try {
      if (data.paymentStatus === 'requires_action') {
        // 3DS completes after the server answered with the pre-payment plan, so settle via a
        // second retry call (a paid no-op by then) to pick up the refreshed plan.
        const settled = await confirmAndSettleRetry(data, retryPayment);
        await this.handleSubscriptionSuccess(settled);
        messagesStore.showSuccessMessage('Payment confirmed and subscription updated successfully');
      } else if (isSubscriptionPaymentIncomplete(data)) {
        throw new PaymentFailedError(data.declineReason ?? GENERIC_PAYMENT_FAILURE_MESSAGE);
      } else {
        await this.handleSubscriptionSuccess(data);
        messagesStore.showSuccessMessage('Payment successful');
      }
    } finally {
      // Refresh on failure too, so the banner reflects the still-open (or newly voided) invoice.
      await this.invoicesStore.getInvoices();
    }
  }

  // Cancels the subscription and rethrows on failure so the caller owns the error
  // UI (mirrors updateSubscriptionV2). Shows the success toast on success only.
  @action.bound
  public async cancelSubscriptionV2() {
    const {
      messagesStore,
      organizationStore: { id: orgId },
    } = this.rootStore;
    await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).put(
      `organizations/${orgId}/subscriptions/cancel`,
    );
    await this.getPlanName();
    await this.getTenantDetail();
    messagesStore.showSuccessMessage('Succefully cancelled subscription');
  }

  // Legacy v1 cancel drawer: swallows the error and toasts (no inline error UI).
  @action.bound
  public async cancelSubscription() {
    try {
      await this.cancelSubscriptionV2();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to cancel subscription');
      this.rootStore.messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public clearInvoicePreview() {
    this.invoicePreview = null;
  }

  @action.bound
  public async getInvoicePreview(stripePriceId: string, promotionCode?: string) {
    const {
      messagesStore,
      organizationStore: { id: orgId },
    } = this.rootStore;
    this.loadingInvoicePreview = true;
    try {
      const response = await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).get(
        `organizations/${orgId}/invoices/preview`,
        { params: { stripePriceId, ...(promotionCode && { promotionCode }) } },
      );
      if (response.data) {
        this.amountDue = response.data.amountDue;
        this.invoicePreview = {
          amountDue: response.data.amountDue,
          transitionType: response.data.transitionType,
          discountAmount: response.data.discountAmount,
          promotionCode: response.data.promotionCode,
          promotion: response.data.promotion,
          planAmount: response.data.planAmount,
        };
      }
    } catch (error) {
      // With a promo code the caller owns the error UI (inline under the promo input); an invalid
      // code must not surface as the generic toast.
      if (promotionCode) {
        throw error;
      }
      messagesStore.showErrorMessage('Unable to get amount due');
    } finally {
      this.loadingInvoicePreview = false;
    }
  }

  @action.bound
  public async deletePaymentMethod(paymentMethod: IPaymentMethod) {
    const {
      messagesStore,
      organizationStore: { id },
    } = this.rootStore;
    try {
      await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).delete(
        `organizations/${id}/paymentMethods/${paymentMethod.id}`,
      );
      messagesStore.showSuccessMessage(
        `You’ve deleted ${paymentMethod.card.brand} ending in ${paymentMethod.card.last4}`,
      );
    } catch (error) {
      messagesStore.showErrorMessage(
        `Failed to delete the ${paymentMethod.card.brand} ending in ${paymentMethod.card.last4}`,
      );
    }
  }

  @computed get nextBillingDate() {
    if (this.billingTenure && this.billingTenure.endDate) {
      return this.billingTenure.endDate;
    }
    return undefined;
  }

  @computed get currentPlan() {
    const { planId } = this.tenantInfo || {};
    return this.plansDetails.find((p) => p.id === planId);
  }

  @computed get isFreeTier() {
    return this.tenantInfo?.planName === 'Free Tier' || false;
  }

  @computed get growthPaidUsageStartAt(): string | null {
    if (this.tenantInfo?.planName !== 'Growth') {
      return null;
    }
    return this.tenantInfo.paidUsageStartAt ?? null;
  }

  @computed get currentBillingCycle(): 'month' | 'year' {
    const plan = this.currentPlan;
    if (!plan || !this.currentPriceId) {
      return 'month';
    }
    const yearlyPrice = plan.prices.find((p) => p.recurringInterval === 'year');
    if (yearlyPrice && this.currentPriceId === yearlyPrice.id) {
      return 'year';
    }
    return 'month';
  }

  @action.bound
  private async handleSubscriptionSuccess(data: SubscriptionResponse) {
    const { featuresStore, organizationStore, trialStore } = this.rootStore;

    if (data.planName && data.planId && this.tenantInfo) {
      this.tenantInfo.planId = data.planId;
      this.tenantInfo.planName = data.planName;
    } else {
      await this.getTenantDetail();
    }
    await featuresStore.getFeatures();
    await organizationStore.loadEventUsageStatus();
    await organizationStore.loadTotalEventsCount();
    await this.getPlanName();
    trialStore.reset();
  }
}
